import { useQueryClient } from "@tanstack/react-query";
import PQueue from "p-queue";
import { useEffect, useState } from "react";
import {
  getListDocumentsQueryKey,
  useCreateUpload,
} from "~/api/default/default";
import {
  useCompleteMultipartUpload,
  useGetMultipartPartUrl,
  useStartMultipartUpload,
} from "~/api/documents/documents";
import { Document } from "~/api/model";
import { getGetOrganizationQueryKey } from "~/api/organizations/organizations";
import { uploadChunkToS3 } from "~/lib/s3";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";

const MAX_CONCURRENT_CHUNKS = 6;
const POLLING_INTERVAL_MS = 10000;
const CHUNK_SIZE = 6 * 1024 * 1024; // 6MB - AWS S3 requires minimum 5MB for multipart uploads (except last part)

interface UseDocumentUploadParams {
  organizationId: string;
  caseId: string;
  sessionBatchKey: string;
}

export const useDocumentUpload = ({
  organizationId,
  caseId,
  sessionBatchKey,
}: UseDocumentUploadParams) => {
  const { mutateAsync: createUpload } = useCreateUpload();
  const { mutateAsync: startMultipartUpload } = useStartMultipartUpload();
  const { mutateAsync: getMultipartPartUrl } = useGetMultipartPartUrl();
  const { mutateAsync: completeMultipartUpload } = useCompleteMultipartUpload();
  const queryClient = useQueryClient();

  const [uploadQueue] = useState(
    () => new PQueue({ concurrency: MAX_CONCURRENT_CHUNKS })
  );

  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );

  const [queuedDocuments, setQueuedDocuments] = useState<
    (Document & { uploadId?: string })[]
  >([]);

  const [processingDocuments, setProcessingDocuments] = useState<Set<string>>(
    new Set()
  );

  const [abortControllersByFilename] = useState<
    Record<string, AbortController>
  >({});

  const handleCreateDocument = async (file: File): Promise<void> => {
    const uploadStartTime = Date.now();

    try {
      const payload = {
        filename: file.name,
        contentType: file.type,
        batchKey: sessionBatchKey,
      };

      logger.info("🔄 UPLOAD PAYLOAD", payload);

      // Set initial progress
      setUploadProgress((prev) => ({ ...prev, [file.name]: 10 }));

      const upload = await createUpload({
        organizationId,
        projectId: caseId,
        data: payload,
      });

      // Replace queued document with uploadId
      setQueuedDocuments((prev) =>
        prev.map((doc) =>
          doc.filename === file.name ? { ...doc, uploadId: upload.id } : doc
        )
      );

      const startResp = await startMultipartUpload({
        organizationId,
        projectId: caseId,
        uploadId: upload.id,
        data: {
          contentType: file.type,
          filename: file.name,
        },
      });

      const { uploadId, key } = startResp;

      const chunkCount = Math.ceil(file.size / CHUNK_SIZE);
      const uploadedParts: { ETag: string; PartNumber: number }[] = [];

      // Prepare abort controller for this file if not present
      if (!abortControllersByFilename[file.name]) {
        abortControllersByFilename[file.name] = new AbortController();
      }
      const abortSignal = abortControllersByFilename[file.name].signal;

      const uploadChunk = async (index: number) => {
        if (abortSignal.aborted) {
          throw new DOMException("Aborted", "AbortError");
        }
        const partNumber = index + 1;
        const start = index * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const partUrlData = await getMultipartPartUrl({
          organizationId,
          projectId: caseId,
          uploadId: upload.id,
          data: {
            key,
            uploadId,
            partNumber,
          },
        });

        const etagHeader = await uploadChunkToS3(
          partUrlData.signedUrl as string,
          chunk,
          file.type,
          undefined,
          abortSignal
        );
        if (!etagHeader) {
          throw new Error("Missing ETag in S3 response");
        }

        uploadedParts.push({
          ETag: etagHeader.replace(/"/g, ""),
          PartNumber: partNumber,
        });

        const progressPct = 20 + Math.round((partNumber / chunkCount) * 40);
        setUploadProgress((prev) => ({ ...prev, [file.name]: progressPct }));
      };

      // Queue each chunk individually through PQueue
      const chunkPromises: Promise<void>[] = [];
      for (let i = 0; i < chunkCount; i++) {
        chunkPromises.push(
          uploadQueue.add(() => uploadChunk(i), { priority: 0 })
        );
      }
      await Promise.all(chunkPromises);

      await completeMultipartUpload({
        organizationId,
        projectId: caseId,
        uploadId: upload.id,
        data: {
          key,
          uploadId,
          parts: uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber),
        },
      });

      setUploadProgress((prev) => ({ ...prev, [file.name]: 60 }));

      // Add to processing documents for gradual progress increase
      setProcessingDocuments((prev) => new Set(prev).add(file.name));

      const uploadDurationMs = Date.now() - uploadStartTime;
      trackEvent({
        eventName: "app/case/upload.process/file_completed",
        properties: {
          filename: file.name,
          fileSizeBytes: file.size,
          uploadDurationMs,
          caseId,
        },
      });

      // Cleanup abort controller after successful upload
      delete abortControllersByFilename[file.name];
    } catch (error) {
      // If aborted intentionally, do not log as error nor mark failed
      if (error instanceof DOMException && error.name === "AbortError") {
        logger.info(`⏹️ Upload aborted for ${file.name}`);
      } else {
        logger.error(`❌ Error uploading document ${file.name}:`, error);

        trackEvent({
          eventName: "app/case/upload.process/file_failed",
          properties: {
            filename: file.name,
            fileSizeBytes: file.size,
            errorMessage:
              error instanceof Error ? error.message : String(error),
            caseId,
          },
        });
      }
      // Remove from queued documents and processing on error
      setQueuedDocuments((prev) =>
        prev.filter((doc) => doc.filename !== file.name)
      );
      setProcessingDocuments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(file.name);
        return newSet;
      });
      if (error && typeof error === "object" && "message" in error) {
        const errorMessage = (error as any).message || "";
        if (
          errorMessage.includes("Free trial limit reached") ||
          errorMessage.includes("Payment required")
        ) {
          queryClient.invalidateQueries({
            queryKey: getGetOrganizationQueryKey(organizationId),
          });
        }
      }
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setUploadProgress((prev) => ({ ...prev, [file.name]: -1 }));
        throw error;
      }
    }
  };

  const hasActiveUploads = uploadQueue.size > 0 || uploadQueue.pending > 0;

  // Has documents waiting to appear in the API (with uploadId)
  const hasWaitingDocuments = queuedDocuments.some((doc) => doc.uploadId);

  // Polling effect for invalidating queries while uploads are active
  useEffect(() => {
    let isCancelled = false;

    const poll = async () => {
      if (isCancelled) {
        return;
      }

      if (!hasActiveUploads && !hasWaitingDocuments) {
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, caseId),
      });

      setTimeout(poll, POLLING_INTERVAL_MS);
    };

    poll();

    return () => {
      isCancelled = true;
    };
  }, [
    hasActiveUploads,
    hasWaitingDocuments,
    caseId,
    organizationId,
    queryClient,
  ]);

  const uploadDocuments = async (files: File[]) => {
    const batchStartTime = Date.now();
    const totalSizeBytes = files.reduce((sum, file) => sum + file.size, 0);

    setUploadProgress(
      files.reduce((acc, file) => ({ ...acc, [file.name]: 0 }), {})
    );

    trackEvent({
      eventName: "app/case/upload.process/started",
      properties: {
        fileCount: files.length,
        totalSizeBytes,
        caseId,
        organizationId,
        batchKey: sessionBatchKey,
      },
    });

    let successCount = 0;
    let failedCount = 0;

    try {
      const results = await Promise.allSettled(files.map(handleCreateDocument));

      results.forEach((result) => {
        if (result.status === "fulfilled") {
          successCount++;
        } else {
          failedCount++;
        }
      });
    } catch (error) {
      logger.error("Upload batch encountered errors", error as Error);
    }

    const totalDurationMs = Date.now() - batchStartTime;

    trackEvent({
      eventName: "app/case/upload.process/batch_completed",
      properties: {
        fileCount: files.length,
        successCount,
        failedCount,
        totalSizeBytes,
        totalDurationMs,
        caseId,
      },
    });

    return null;
  };

  // Upload queue event handlers
  useEffect(() => {
    const onIdle = () => {};

    const onError = (error: Error) => {
      logger.error("❌ Upload error:", error);
    };

    uploadQueue.on("idle", onIdle);
    uploadQueue.on("error", onError);

    return () => {
      uploadQueue.off("idle", onIdle);
      uploadQueue.off("error", onError);
    };
  }, [uploadQueue]);

  const cancelDraftUpload = (filename: string) => {
    const currentProgress = uploadProgress[filename] || 0;

    trackEvent({
      eventName: "app/case/upload.process/file_cancelled",
      properties: {
        filename,
        progressPercent: currentProgress,
        caseId,
      },
    });

    const controller = abortControllersByFilename[filename];
    if (controller) {
      controller.abort();
      delete abortControllersByFilename[filename];
    }

    setQueuedDocuments((prev) =>
      prev.filter((doc) => doc.filename !== filename)
    );
    setProcessingDocuments((prev) => {
      const next = new Set(prev);
      next.delete(filename);
      return next;
    });
    setUploadProgress((prev) => {
      const next = { ...prev };
      delete next[filename];
      return next;
    });
  };

  // Gradually increase progress for processing documents
  useEffect(() => {
    if (processingDocuments.size === 0) return;

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        processingDocuments.forEach((filename) => {
          const currentProgress = newProgress[filename] || 60;
          if (currentProgress < 90) {
            // Gradually increase from 60% to 90% over time
            newProgress[filename] = Math.min(90, currentProgress + 2);
          }
        });
        return newProgress;
      });
    }, 500);

    return () => clearInterval(interval);
  }, [processingDocuments]);

  return {
    uploadDocuments,
    cancelDraftUpload,
    uploadProgress,
    queuedDocuments,
    setQueuedDocuments,
    processingDocuments,
    setProcessingDocuments,
    setUploadProgress,
    hasActiveUploads,
    hasWaitingDocuments,
    pendingUploads: uploadQueue.size,
    activeUploads: uploadQueue.pending,
  };
};
